angular.module('stack.common.ui.application.stackApplication', [
  'ui.router',
  'global'
])

/**
 * The Application directive is an application container that wraps
 * the global header, footer, subfooter and application views. This
 * directive sits at the root of the application.
 *
 * @class Application
 * @constructor
 * @module stack.common.ui.application.stackApplication
 * @example
 *   <stack-application></stack-application>
 */
.directive('stackApplication', [
  '$document',
  '$state',
  '$timeout',
  '$window',
  'GLOBAL',
  'FocusService',
  function ($document, $state, $timeout, $window, GLOBAL, FocusService) {
    'use strict';

    return {
      restrict: 'A',
      link: function (scope, element, attrs) {
        var cloak;
        /**
        * Property houses the initial namespaced state.
        * This property is used to define a clean state
        * on the namespaced scope object.
        *
        * @property namespace
        * @type {Object}
        * @private
        */
        var namespace = {
          route: '',
          loading: false,
          mask: false
        };

        /**
         * Property houses scoped namespaced object.
         *
         * @property application
         * @type {Object}
         */
        scope.application = {};

        /**
         * Method redirects the user.
         *
         * @param {string} path  -Path to evaluate
         * @private
         */
        function redirect(path) {
          // Redirect.
          $state.go(!path ? GLOBAL.routes.unauthenticated : path);
        }

        /**
         * Method renders the application loader.
         *
         * @private
         */
        function applicationLoaderShow() {
          cloak.show();
          scope.application.loading = true;
          $window.scrollTo(0, 0);
        }

        /**
         * Method hides the application loader.
         *
         * @param {string} routeName - Route name
         * @private
         */
        function applicationLoaderHide(routeName) {
          var route = routeName;
          // cloak.hide();
          var t = $timeout(function () {
            var activeElement;
            if (cloak.is(':visible')) {
              activeElement = angular.element($document.activeElement);

              // Only focus the header when nothing else on the page has been focused.
              if (!activeElement.is('button,a,input')) {
                // Focus tabs when appropriate. Otherwise, always begin
                // the page's focus on the header brand.
                route = angular.isUndefined(route) ? 'dashboard' : route;
                if (route === 'dashboard' || route === 'help') {
                  FocusService.focus('header:' + route + ':focus');
                } else {
                  FocusService.focus('header:brand:focus');
                }
              }

              $window.scrollTo(0, 0);
              scope.application.loading = false;
              cloak.hide();
            }

            $timeout.cancel(t);
          }, 1000);
        }

        /**
         * Method is triggered when a state transition begins.
         *
         * @private
         */
        function onRouteChangeStart() {
          // TODO remove this function?
        }

        /**
         * Method is triggered when a state transition completes.
         *
         * @param {Object} evt - Event object.
         * @param {Object} toState - The state being transitioned to.
         * @param {Object} toParams - The params supplied to the toState.
         * @param {Object} fromState - The current state, pre-transition.
         * @private
         */
        function onRouteChangeSuccess(evt, toState, toParams, fromState) {
          var route = toState.name;
          $state.previous = fromState;
          scope.application.route = route;
        }

        /**
         * Method is triggered when any resolve promises are rejected.
         *
         * @param {Object} evt - Event object.
         * @param {Object} toState - The state being transitioned to.
         * @param {Object} toParams - The params supplied to the toState.
         * @param {Object} fromState - The current state, pre-transition.
         * @param {Object} fromParams - The params supplied to the fromState.
         * @param {Object} rejection - The resolve error object.
         * @private
         */
        function onRouteChangeError(evt, toState, toParams, fromState, fromParams, rejection) {
          if (rejection && {}.hasOwnProperty.call(rejection, 'path')) {
            redirect(rejection.path);

            // In some cases the redirect 'path' can equal the 'current'
            // path. When this happens, the ui-router implementation
            // (i.e., $state.go()) does not redirect or refresh the interface.
            // When this happens the loader is never hidden due to the fact
            // that the $stateChangeSuccess event is never fired. The below
            // code block resolves this case.
            if (rejection.path === $state.current.name) {
              applicationLoaderHide();
            }
          }
        }

        /**
         * Method is triggered when a user's authentication session expires.
         * The user is redirected to the landing screen when the session
         * expires.
         *
         * @param {Object} evt - Event object
         * @param {Object} route - The path to redirect toward.
         * @private
         */
        function onAuthenticationTimeout(evt, route) {
          if (!$state === route.path) {
            redirect(route.path);
          }
        }

        /**
         * Method handler triggered by the 'applicationloader:show' event.
         *
         * @private
         */
        function onApplicationLoaderShow() {
          applicationLoaderShow();
        }

        /**
         * Method handler triggered by the 'applicationloader:hide' event.
         *
         * @private
         */
        function onApplicationLoaderHide() {
          applicationLoaderHide();
        }

        /**
         * Method handler triggered by the 'applicationmask:show' event.
         *
         * @private
         */
        function onApplicationMaskShow() {
          scope.application.mask = true;
          cloak.show();
        }

        /**
         * Method handler triggered by the 'applicationmask:hide' event.
         *
         * @private
         */
        function onApplicationMaskHide() {
          cloak.hide();
          scope.application.mask = false;
        }

        /**
         * Method defines component properties during the
         * initialization phase.
         *
         * @private
         */
        function defineProperties() {
          scope.application = angular.extend({}, namespace);
          attrs.stackApplication = angular
            .isDefined(attrs.stackApplication)
            ? attrs.stackApplication
            : '#applicationCloak';
        }

        /**
         * Method defines application-level (i.e., global) constants.
         *
         * @private
         */
        function defineConstants() {
          scope.application = angular.extend(scope.application, GLOBAL);
        }

        /**
         * Method performs DOM manipulation during the
         * initialization phase.
         *
         * @private
         */
        function prepareDOM() {
          element.addClass('stack-application');
          cloak = element.find(attrs.stackApplication);
        }

        /**
         * Captures keyboard tab events when loading
         * or mask is active to prevent navigation
         * behind the mask
         *
         * @private
         */
        function keyboardListeners() {
          $window.addEventListener('keydown', function (event) {
            if (event.keyCode === 9 && (scope.application.loading || scope.application.mask)) {
              event.preventDefault();
            }
          });
        }

        /**
         * Method initializes event-based listeners.
         *
         * @private
         */
        function eventListeners() {
          scope.$on('$stateChangeStart', onRouteChangeStart);
          scope.$on('$stateChangeSuccess', onRouteChangeSuccess);
          scope.$on('$stateChangeError', onRouteChangeError);
          scope.$on('authentication:timeout', onAuthenticationTimeout);
          scope.$on('applicationloader:show', onApplicationLoaderShow);
          scope.$on('applicationloader:hide', onApplicationLoaderHide);
          scope.$on('applicationmask:show', onApplicationMaskShow);
          scope.$on('applicationmask:hide', onApplicationMaskHide);
        }

        /**
         * Method exectues initialization process.
         *
         * @private
         */
        function initialize() {
          defineProperties();
          defineConstants();
          prepareDOM();
          eventListeners();
          keyboardListeners();
        }
        initialize();
      }
    };
  }
]);
